import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AlphabeticalCustomerList from './alphabeticalcustomerlist';
import Spinner from '../common/spinner';
import { CUSTOMER_VIEW_FILTER_UPDATE } from '../modules/customerdata';
import FooterPadding from '../main/footerpadding';
import Footer from '../main/footer';
import { Grid, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import TabPanel from '../common/tabpanel';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import CustomershipStatus from '../domain/customershipstatus';
import Alert from '@mui/material/Alert';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import EuroIcon from '@mui/icons-material/Euro';
import Tooltip from '@mui/material/Tooltip';
import ValueSortedCustomerGroup from './valuesortedcustomergroup';
import SearchInput from '../common/searchinput';
import { useTranslation } from 'react-i18next';
import { getCustomerDescriptiveInfo } from '../modules/customerdata';
import CustomerItem from './customeritem';
import { getAlphabeticallySorted } from './alphabeticalcustomerlist';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';
import LeadStage, { buildLeadStageOptions } from '../domain/leadstage';
import { currencySymbolFor } from '../common/common';
import { useActiveTenantCurrency } from '../common/currencyutils';

const _ = require('lodash');

const CustomerSearch = (props) => {
    return (
        <Grid xs={8} md={6} item>
            <SearchInput searchInput={props.customerFilter} onChange={props.onFilterChange} />
        </Grid>
    );
};

const GroupBySelector = (props) => {
    const { t } = useTranslation();

    return (
        <Grid item xs={4} md={6}>
            <ToggleButtonGroup
                sx={{ margin: '5px', float: 'right' }}
                exclusive
                value={props.orderBy}
                onChange={props.onOrderByChange}
                size={'small'}>
                <ToggleButton value="abc">
                    <Tooltip title={t('general.alphabeticalOrder')}>
                        <SortByAlphaIcon sx={{ margin: '5px' }} />
                    </Tooltip>
                </ToggleButton>

                <ToggleButton value="money">
                    <Tooltip title={t('general.sortByCustomerValue')}>
                        <EuroIcon sx={{ margin: '5px' }} />
                    </Tooltip>
                </ToggleButton>
            </ToggleButtonGroup>
        </Grid>
    );
};

const getLeadStageLabel = (stage, stageOptions) => {
    const option = (stageOptions || []).find((o) => o.value === stage);
    return option ? option.label : null;
};

const enrichWithLeadStageLabel = (customers, stageOptions, leadStageLookup) =>
    (customers || []).map((customer) => {
        const leadStage =
            customer.leadStage !== undefined
                ? customer.leadStage
                : leadStageLookup && leadStageLookup[customer.id]
                ? leadStageLookup[customer.id].leadStage
                : null;
        return {
            ...customer,
            leadStage,
            leadStageLabel: leadStage != null ? getLeadStageLabel(leadStage, stageOptions) : null,
        };
    });

const filterByLeadStage = (customers, leadStageFilter) =>
    (customers || []).filter((customer) => {
        if (leadStageFilter === 'all') return true;
        return String(customer.leadStage) === String(leadStageFilter);
    });

const getLeadStageCounts = (customers, stageOptions) => {
    const counts = { all: 0 };
    for (const option of stageOptions || []) {
        counts[String(option.value)] = 0;
    }

    for (const customer of customers || []) {
        if (customer == null) continue;
        counts.all++;
        if (customer.leadStage != null) {
            const key = String(customer.leadStage);
            if (counts[key] != null) counts[key]++;
        }
    }

    return counts;
};

const LeadStageSelector = (props) => {
    const { t } = useTranslation();

    return (
        <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
            <strong>{t('customer.leadStage')}</strong>
            <ToggleButtonGroup
                sx={{ margin: '5px 0', display: 'flex', flexWrap: 'wrap', gap: 1 }}
                exclusive
                value={props.leadStageFilter}
                onChange={props.onLeadStageFilterChange}
                size="small">
                {props.options.map((option) => (
                    <ToggleButton key={option.value} value={option.value}>
                        {option.label} ({option.count})
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Grid>
    );
};

const SelectedCustomerListView = (props) => {
    const { t } = useTranslation();
    const currency = useActiveTenantCurrency();

    if (props.orderBy === 'abc')
        return (
            <AlphabeticalCustomerList
                customerDescriptions={props.customers}
                filter={props.filter}
                history={props.history}
                showLeadStageBadge={props.showLeadStageBadge}
            />
        );
    else if (props.orderBy === 'money') {
        const symbol = currencySymbolFor(currency);
        const viewConfig = [
            { title: `${t('general.over')} 50 000${symbol}`, upperLimit: undefined, lowerLimit: 50000 },
            { title: `10 000${symbol} - 50 000${symbol}`, upperLimit: 50000, lowerLimit: 10000 },
            { title: `1 000${symbol} - 10 000${symbol}`, upperLimit: 10000, lowerLimit: 1000 },
            { title: `${t('general.under')} 1 000${symbol}`, upperLimit: 1000, lowerLimit: undefined },
        ];

        if (props.customerValuations == null) return null;

        const filteredCustomers = _.filter(props.customerValuations, (c) => {
            return _.deburr(c.name).toLowerCase().indexOf(_.deburr(props.filter).toLowerCase()) !== -1;
        });
        let viewGroups = [];
        for (const g of viewConfig) {
            viewGroups.push({
                title: g.title,
                customers: _.orderBy(
                    _.filter(filteredCustomers, function (i) {
                        if (g.upperLimit && g.lowerLimit)
                            return i.valuation >= g.lowerLimit && i.valuation < g.upperLimit;
                        else if (g.upperLimit) return i.valuation < g.upperLimit;
                        else if (g.lowerLimit) return i.valuation >= g.lowerLimit;
                    }).map((c) => {
                        return {
                            name: c.name,
                            id: c.id,
                            value: c.valuation,
                            leadStage: c.leadStage,
                            leadStageLabel: c.leadStageLabel,
                        };
                    }),
                    ['value'],
                    ['desc']
                ),
            });
        }

        return (
            <Grid item xs={12}>
                {viewGroups.map((g) => (
                    <ValueSortedCustomerGroup
                        key={g.title}
                        group={g}
                        history={props.history}
                        showLeadStageBadge={props.showLeadStageBadge}
                    />
                ))}
            </Grid>
        );
    }

    return null;
};

const SearchStatus = (props) => {
    const { t } = useTranslation();

    return (
        <div>
            {(props.getCustomerDescriptionsInProgress || props.customershipValuationsFetching) && (
                <Spinner padding="20px" title={t('general.loading')} />
            )}
            {(!props.getCustomerDescriptionsInProgress && props.getCustomerDescriptionsFailed) ||
                (!props.customershipValuationsFetching && props.customershipValuationsFailed && (
                    <div style={{ padding: '20px' }}>
                        <Alert severity="error">{t('customer.failedToRetrieveCustomerDataRetry')}</Alert>
                    </div>
                ))}
        </div>
    );
};

// "Potentiaaliset" = legacy potential customers, not (yet) real leads.
function getPotentialCustomers(customers) {
    if (customers == null) return null;
    return _.filter(customers, { status: CustomershipStatus.Potential() });
}

// "Liidit" = customers with the authoritative Lead status OR converted Won leads.
function getLeadCustomers(customers) {
    if (customers == null) return null;
    return _.filter(
        customers,
        (c) => c.status === CustomershipStatus.Lead() || (c != null && c.leadStage === LeadStage.Won())
    );
}

const OtherCustomersView = (props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const custCount = !!props.customers ? props.customers.length : 0;
    if (custCount === 0) return null;
    const filter = props.filter || '';
    const filteredCustomers = getAlphabeticallySorted(props.customers, filter);

    const customerCountStr = () => {
        let str = '';
        if (filter.length > 0) {
            str += filteredCustomers.length + '/';
        }
        str += custCount;
        return str;
    };
    return (
        <Grid item xs={12}>
            <Accordion sx={{ marginTop: '3em' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <strong>
                        {t('customer.otherCustomers')} {customerCountStr()} {t('pcs')}
                    </strong>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{
                        backgroundColor: 'action.hover',
                        padding: 2,
                        borderLeft: (theme) => '4px solid ' + theme.palette.primary.main,
                        borderRadius: 1,
                        width: '100%',
                        boxSizing: 'border-box',
                    }}>
                        <Grid container>
                            {filteredCustomers.map((item) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
                                    <h2>{item.key}</h2>
                                    {item.customers.map((customer) => (
                                        <CustomerItem
                                            key={customer.id}
                                            customerDesc={customer}
                                            history={history}
                                            showLeadStageBadge={props.showLeadStageBadge}
                                        />
                                    ))}
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

const PotentialCustomers = (props) => {
    const history = useHistory();
    const { t } = useTranslation();
    const showLeadStageUi = props.showLeadStageUi !== false;
    const enrichedOwn = enrichWithLeadStageLabel(props.customersOwn, props.stageOptions, props.leadStageLookup);
    const enrichedOther = enrichWithLeadStageLabel(props.customersOther, props.stageOptions, props.leadStageLookup);
    const enrichedValuations = enrichWithLeadStageLabel(props.valuations, props.stageOptions, props.leadStageLookup);
    const filteredOwn = showLeadStageUi ? filterByLeadStage(enrichedOwn, props.leadStageFilter) : enrichedOwn;
    const filteredOther = showLeadStageUi ? filterByLeadStage(enrichedOther, props.leadStageFilter) : enrichedOther;
    const filteredValuations = showLeadStageUi
        ? filterByLeadStage(enrichedValuations, props.leadStageFilter)
        : enrichedValuations;
    const leadStageOptions = [
        { value: 'all', label: t('general.all'), count: props.leadStageCounts.all },
        ...(props.stageOptions || []).map((option) => ({
            value: String(option.value),
            label: option.label,
            count: props.leadStageCounts[String(option.value)],
        })),
    ];

    return (
        <Grid container>
            <CustomerSearch
                customerFilter={props.customerFilter}
                onFilterChange={props.onFilterChange}
            />
            <GroupBySelector
                orderBy={props.orderBy}
                onOrderByChange={props.onOrderByChange}
            />
            {showLeadStageUi && (
                <LeadStageSelector
                    leadStageFilter={props.leadStageFilter}
                    onLeadStageFilterChange={props.onLeadStageFilterChange}
                    options={leadStageOptions}
                />
            )}
            <SelectedCustomerListView
                customers={filteredOwn}
                customerValuations={filteredValuations}
                filter={props.customerFilter}
                history={history}
                orderBy={props.orderBy}
                showLeadStageBadge={showLeadStageUi}
            />

            <OtherCustomersView
                customers={filteredOther}
                filter={props.customerFilter}
                showLeadStageBadge={showLeadStageUi}
            />

            <SearchStatus
                getCustomerDescriptionsInProgress={props.getCustomerDescriptionsInProgress}
                customershipValuationsFetching={props.customershipValuationsFetching}
                getCustomerDescriptionsFailed={props.getCustomerDescriptionsFailed}
                customershipValuationsFailed={props.customershipValuationsFailed}
            />
        </Grid>
    );
};

const ActiveCustomers = (props) => {
    const history = useHistory();

    return (
        <Grid container>
            <CustomerSearch
                customerFilter={props.customerFilter}
                onFilterChange={props.onFilterChange}
            />
            <GroupBySelector
                orderBy={props.orderBy}
                onOrderByChange={props.onOrderByChange}
            />

            <SelectedCustomerListView
                customers={props.customersOwn}
                customerValuations={props.valuations}
                filter={props.customerFilter}
                history={history}
                orderBy={props.orderBy}
                showLeadStageBadge={false}
            />

            <OtherCustomersView customers={props.customersOther} filter={props.customerFilter} showLeadStageBadge={false} />

            <SearchStatus
                getCustomerDescriptionsInProgress={props.getCustomerDescriptionsInProgress}
                customershipValuationsFetching={props.customershipValuationsFetching}
                getCustomerDescriptionsFailed={props.getCustomerDescriptionsFailed}
                customershipValuationsFailed={props.customershipValuationsFailed}
            />
        </Grid>
    );
};

export default function Customers(props) {
    const dispatch = useDispatch();
    const history = useHistory();
    const [selectedTab, setSelectedTab] = React.useState(2);
    const [orderBy, setOrderBy] = React.useState('abc');
    const [leadTabStageFilter, setLeadTabStageFilter] = React.useState('all');
    const customerFilter = useSelector((state) => state.customerdata.customerFilter);
    const customerDescriptions = useSelector((state) => state.customerdata.customerDescriptions);
    const customerDescriptionsOwn = useSelector((state) => state.customerdata.customerDescriptionsOwn);
    const customershipValuations = useSelector((state) => state.customerdata.customershipValuations);
    const getCustomerDescriptionsInProgress = useSelector(
        (state) => state.customerdata.getCustomerDescriptionsInProgress
    );
    const customershipValuationsFetching = useSelector((state) => state.customerdata.customershipValuationsFetching);
    const getCustomerDescriptionsFailed = useSelector((state) => state.customerdata.getCustomerDescriptionsFailed);
    const customershipValuationsFailed = useSelector((state) => state.customerdata.customershipValuationsFailed);
    const configurableLeadStages = useSelector((state) =>
        state.parameterdata && state.parameterdata.parameters ? state.parameterdata.parameters.LeadStage : null
    );
    const { t } = useTranslation();
    const stageOptions = buildLeadStageOptions(configurableLeadStages, t);
    const customersOthers =
        customerDescriptions && customerDescriptions.length > 0
            ? customerDescriptions.filter((desc) => !customerDescriptionsOwn.find((o) => o.id === desc.id))
            : customerDescriptions;
    const leadStageLookup = {};
    for (const customer of customerDescriptions || []) {
        leadStageLookup[customer.id] = customer;
    }
    const potentialCustomersAll = enrichWithLeadStageLabel(getPotentialCustomers(customerDescriptionsOwn), stageOptions, leadStageLookup);
    const leadCustomersOwn = enrichWithLeadStageLabel(getLeadCustomers(customerDescriptionsOwn), stageOptions, leadStageLookup);
    const potentialLeadStageCounts = getLeadStageCounts(potentialCustomersAll, stageOptions);
    const leadTabLeadStageCounts = getLeadStageCounts(leadCustomersOwn, stageOptions);

    const onClickNewCustomer = () => {
        history.push('/customers/edit/new');
    };

    const onClickNewLead = () => {
        history.push('/customers/edit/new', { asLead: true });
    };

    const leadFeatureEnabled = configurableLeadStages && configurableLeadStages.length > 0;

    const onFilterChange = (evt) => {
        dispatch({ type: CUSTOMER_VIEW_FILTER_UPDATE, filter: evt.target.value });
    };

    useEffect(() => {
        dispatch(getCustomerDescriptiveInfo());
        switch (history.location.hash) {
            case '#active-abc':
                setSelectedTab(2);
                setOrderBy('abc');
                break;
            case '#leads-abc':
                setSelectedTab(1);
                setOrderBy('abc');
                break;
            case '#potential-abc':
                setSelectedTab(0);
                setOrderBy('abc');
                break;
            case '#active-money':
                setSelectedTab(2);
                setOrderBy('money');
                break;
            case '#leads-money':
                setSelectedTab(1);
                setOrderBy('money');
                break;
            case '#potential-money':
                setSelectedTab(0);
                setOrderBy('money');
                break;
            default:
                history.replace('/customers');
                break;
        }
    }, [dispatch, history]);

    const getActiveCustomers = (customers) => {
        if (customers !== null) return _.filter(customers, { status: CustomershipStatus.Active() });

        return null;
    };

    const tabHash = (tab) => {
        switch (tab) {
            case 0:
                return 'potential';
            case 1:
                return 'leads';
            case 2:
                return 'active';
            default:
                return 'potential';
        }
    };

    const setUrlHash = (tab, order) => {
        setTimeout(() => {
            history.replace(`/customers#${tabHash(tab)}-${order}`);
        }, 1);
    };

    const changeTab = (event, value) => {
        setSelectedTab(value);
        history.replace(`/customers#${tabHash(value)}-${orderBy}`);
    };

    const onOrderByChange = (evt, newOrder) => {
        if (newOrder && newOrder !== orderBy) {
            setOrderBy(newOrder);
            setUrlHash(selectedTab, newOrder);
        }
    };


    const onLeadTabStageFilterChange = (evt, newFilter) => {
        if (newFilter != null) {
            setLeadTabStageFilter(newFilter);
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <AppBar position="static" color="default">
                <Box display="flex" justifyContent="center" width="100%">
                    <Tabs
                        value={selectedTab}
                        onChange={changeTab}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto">
                        <Tab value={0} label={t('general.potentials')} />
                        <Tab value={1} label={t('general.leads')} />
                        <Tab value={2} label={t('general.actives')} />
                    </Tabs>
                </Box>
            </AppBar>
            <TabPanel value={selectedTab} index={0}>
                <PotentialCustomers
                    customerFilter={customerFilter}
                    orderBy={orderBy}
                    onFilterChange={onFilterChange}
                    onOrderByChange={onOrderByChange}
                    showLeadStageUi={false}
                    leadStageCounts={potentialLeadStageCounts}
                    leadStageLookup={leadStageLookup}
                    stageOptions={stageOptions}
                    customersOther={getPotentialCustomers(customersOthers)}
                    customersOwn={getPotentialCustomers(customerDescriptionsOwn)}
                    valuations={getPotentialCustomers(customershipValuations)}
                    getCustomerDescriptionsInProgress={getCustomerDescriptionsInProgress}
                    customershipValuationsFetching={customershipValuationsFetching}
                    getCustomerDescriptionsFailed={getCustomerDescriptionsFailed}
                    customershipValuationsFailed={customershipValuationsFailed}
                />
            </TabPanel>
            <TabPanel value={selectedTab} index={1}>
                <PotentialCustomers
                    customerFilter={customerFilter}
                    orderBy={orderBy}
                    onFilterChange={onFilterChange}
                    onOrderByChange={onOrderByChange}
                    onLeadStageFilterChange={onLeadTabStageFilterChange}
                    leadStageFilter={leadTabStageFilter}
                    leadStageCounts={leadTabLeadStageCounts}
                    leadStageLookup={leadStageLookup}
                    stageOptions={stageOptions}
                    customersOther={getLeadCustomers(customersOthers)}
                    customersOwn={getLeadCustomers(customerDescriptionsOwn)}
                    valuations={getLeadCustomers(customershipValuations)}
                    getCustomerDescriptionsInProgress={getCustomerDescriptionsInProgress}
                    customershipValuationsFetching={customershipValuationsFetching}
                    getCustomerDescriptionsFailed={getCustomerDescriptionsFailed}
                    customershipValuationsFailed={customershipValuationsFailed}
                />
            </TabPanel>
            <TabPanel value={selectedTab} index={2}>
                <ActiveCustomers
                    customerFilter={customerFilter}
                    orderBy={orderBy}
                    onFilterChange={onFilterChange}
                    onOrderByChange={onOrderByChange}
                    customersOther={getActiveCustomers(customersOthers)}
                    customersOwn={getActiveCustomers(customerDescriptionsOwn)}
                    valuations={getActiveCustomers(customershipValuations)}
                    getCustomerDescriptionsInProgress={getCustomerDescriptionsInProgress}
                    customershipValuationsFetching={customershipValuationsFetching}
                    getCustomerDescriptionsFailed={getCustomerDescriptionsFailed}
                    customershipValuationsFailed={customershipValuationsFailed}
                />
            </TabPanel>

            <Footer>
                <Grid item>
                    <Button name="btn-new-customer" variant="contained" color="primary" onClick={onClickNewCustomer}>
                        {t('buttons.newCustomer')}
                    </Button>
                </Grid>
                {leadFeatureEnabled && (
                    <Grid item>
                        <Button name="btn-new-lead" variant="contained" color="primary" onClick={onClickNewLead}>
                            {t('buttons.newLead')}
                        </Button>
                    </Grid>
                )}
            </Footer>
            <FooterPadding />
        </div>
    );
}
